import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { request } from "app/network";
import Swal from "sweetalert2";

interface ReorderFaqModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    faqs?: Array<{ seqId: number; question: string; displayOrder: number }>;
    darkMode?: boolean;
}

interface SortableFaqItemProps {
    id: string;
    question: string;
    darkMode: boolean;
}

function SortableFaqItem({ id, question, darkMode }: SortableFaqItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: darkMode
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                cursor: "grab",
                "&:active": {
                    cursor: "grabbing",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight="regular"
                sx={{
                    color: darkMode
                        ? "rgba(255, 255, 255, 0.8)"
                        : "#344767",
                }}
            >
                {question}
            </MDTypography>
        </MDBox>
    );
}

function ReorderFaqModal({ open, onClose, onSuccess, faqs = [], darkMode = false }: ReorderFaqModalProps) {
    const [orderedFaqs, setOrderedFaqs] = useState<Array<{ seqId: number; question: string; displayOrder: number }>>([]);
    const [loading, setLoading] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedFaqs((items) => {
                const oldIndex = items.findIndex((item) => `faq-${item.seqId}` === active.id);
                const newIndex = items.findIndex((item) => `faq-${item.seqId}` === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // 각 FAQ의 displayOrder를 업데이트
            const updates = orderedFaqs.map((faq, index) => ({
                seqId: faq.seqId,
                displayOrder: index + 1,
            }));

            // 순차적으로 업데이트 (병렬 처리 시 순서 보장 안됨)
            for (const update of updates) {
                await request("Faq.updateDisplayOrder", {
                    paramz: {
                        SeqId: update.seqId,
                        DisplayOrder: update.displayOrder,
                    },
                });
            }

            Swal.fire({
                title: "성공",
                text: "FAQ 순서가 변경되었습니다.",
                icon: "success",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("FAQ 순서 변경 실패:", error);
            Swal.fire({
                title: "오류",
                text: error.message || "FAQ 순서 변경에 실패했습니다.",
                icon: "error",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch and Initialize Data
    React.useEffect(() => {
        const fetchAllFaqs = async () => {
            try {
                setLoading(true);
                // Fetch all FAQs for reordering (PageSize: 1000 to get all)
                const response = await request("Faq.pagedList", {
                    paramz: { PageNumber: 1, PageSize: 1000 }
                });

                if (response?.data?.items) {
                    const items = response.data.items.map((item: any) => ({
                        seqId: item.seqId,
                        question: item.question,
                        displayOrder: item.displayOrder || 0
                    }));
                    setOrderedFaqs(items.sort((a: any, b: any) => a.displayOrder - b.displayOrder));
                }
            } catch (error) {
                console.error("Failed to fetch FAQs for reordering:", error);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            if (faqs && faqs.length > 0) {
                setOrderedFaqs(faqs.sort((a, b) => a.displayOrder - b.displayOrder));
            } else {
                fetchAllFaqs();
            }
        }
    }, [open, faqs]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? "#1a2035" : "#fff",
                    color: darkMode ? "#fff" : "#000",
                },
            }}
        >
            <DialogTitle>FAQ 순서 변경</DialogTitle>
            <DialogContent>
                <MDBox pt={2}>
                    <MDTypography
                        variant="caption"
                        fontWeight="regular"
                        sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                        mb={2}
                        display="block"
                    >
                        드래그하여 FAQ 순서를 변경하세요
                    </MDTypography>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={orderedFaqs.map((faq) => `faq-${faq.seqId}`)}
                            strategy={verticalListSortingStrategy}
                        >
                            {orderedFaqs.map((faq) => (
                                <SortableFaqItem
                                    key={faq.seqId}
                                    id={`faq-${faq.seqId}`}
                                    question={faq.question}
                                    darkMode={darkMode}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="outlined" color="secondary">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default ReorderFaqModal;
